import { useState, useRef, useEffect, useCallback } from "react";
import { useAppAuth } from "@/components/auth/AuthProvider";

import { signalingClient } from "@/lib/signaling";
import { debugLog, debugError } from "@/lib/logger";
import { setStreamingActive } from "@/lib/streamingState";
import type { ConnectionState, DeviceRole } from "@/types";
import {
  createBabyPeerConnection,
  createParentPeerConnection,
  ensureReceiveOnlyTransceivers,
  flushPendingIceCandidates,
  syncBabyPeerConnectionTracks,
  updateBabyConnectionState,
} from "@/features/webrtc/peerConnectionHelpers";
import type { PeerConnectionInfo } from "@/features/webrtc/types";

// Re-export for convenience
export type { ConnectionState };

// Connection retry configuration
const OFFER_RETRY_DELAY_MS = 2000; // Wait 2s for answer before retrying (reduced from 5s for faster connection)
const MAX_OFFER_RETRIES = 3; // Retry up to 3 times

/**
 * WebRTC Hook - Handles peer-to-peer video/audio streaming
 *
 * SIMPLIFIED VERSION:
 * - Stream mode is fixed once started (no mid-stream track changes)
 * - Baby mode: Accept connections from multiple parents, stream local media
 * - Parent mode: Connect to a single baby device, receive remote stream
 */
export function useWebRTC({
  role,
  localStream,
  onRemoteStream,
  onVideoTrackReceived,
  onParentAudioTrack,
  onParentTalkState,
}: {
  role: DeviceRole;
  localStream?: MediaStream | null;
  onRemoteStream?: (stream: MediaStream) => void;
  onVideoTrackReceived?: () => void;
  onParentAudioTrack?: (track: MediaStreamTrack, stream: MediaStream) => void; // Baby receives parent audio
  onParentTalkState?: (talking: boolean, parentId: string) => void; // Baby gets notified of parent talk state
}) {
  const { getToken } = useAppAuth();
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("idle");
  const [error, _setError] = useState<string | null>(null);

  // For PARENT: single peer connection (connects to one baby)
  const peerConnection = useRef<RTCPeerConnection | null>(null);

  // For BABY: Multiple peer connections (one per parent viewer)
  const peerConnectionsMap = useRef<Map<string, PeerConnectionInfo>>(new Map());

  // Track number of connected peers (for baby to know how many parents watching)
  const [connectedPeersCount, setConnectedPeersCount] = useState(0);

  // Track if signaling is connected
  const signalingConnectedRef = useRef(false);

  // Keep latest callbacks in refs to avoid effect recreation
  const onRemoteStreamRef = useRef(onRemoteStream);
  useEffect(() => {
    onRemoteStreamRef.current = onRemoteStream;
  }, [onRemoteStream]);

  const onVideoTrackReceivedRef = useRef(onVideoTrackReceived);
  useEffect(() => {
    onVideoTrackReceivedRef.current = onVideoTrackReceived;
  }, [onVideoTrackReceived]);

  const onParentAudioTrackRef = useRef(onParentAudioTrack);
  useEffect(() => {
    onParentAudioTrackRef.current = onParentAudioTrack;
  }, [onParentAudioTrack]);

  const onParentTalkStateRef = useRef(onParentTalkState);
  useEffect(() => {
    onParentTalkStateRef.current = onParentTalkState;
  }, [onParentTalkState]);

  // Keep track of connected peer for parent mode
  const remotePeerIdRef = useRef<string | null>(null);

  // Parent talk mode state and refs
  const [isTalking, setIsTalking] = useState(false);
  const parentMicStreamRef = useRef<MediaStream | null>(null);

  // Keep track of local stream in ref for synchronous access in handlers
  const localStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    localStreamRef.current = localStream ?? null;

    // Handle track updates (e.g. camera switching)
    if (role === "baby" && localStream) {
      syncBabyPeerConnectionTracks(peerConnectionsMap.current, localStream);
    }
  }, [localStream, role]);

  // Queue pending offers when stream isn't ready yet (for baby device)
  const pendingOffersRef = useRef<
    Map<string, { sourceId: string; sdp: RTCSessionDescriptionInit }>
  >(new Map());

  // Queue for ICE candidates for parent mode (single connection)
  const pendingIceCandidatesRef = useRef<RTCIceCandidateInit[]>([]);

  // Offer retry tracking (parent mode)
  const answerReceivedRef = useRef<boolean>(false);
  const offerRetryCountRef = useRef<number>(0);
  const offerRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const currentTargetIdRef = useRef<string | null>(null);

  // ============================================================
  // Helper Functions
  // ============================================================

  /**
   * Flush pending ICE candidates to a peer connection
   */
  /**
   * Update connected peers count for baby mode
   */
  const updateConnectedPeersCount = useCallback(() => {
    updateBabyConnectionState({
      role,
      connections: peerConnectionsMap.current,
      setConnectedPeersCount,
      setConnectionState,
    });
  }, [role]);

  // ============================================================
  // Baby Mode: Multi-peer handling
  // ============================================================

  /**
   * Create a peer connection for a specific parent (baby mode)
   */
  const createPeerConnectionForParent = useCallback(
    (parentId: string): RTCPeerConnection => {
      return createBabyPeerConnection({
        parentId,
        localStream: localStreamRef.current,
        peerConnectionsMap: peerConnectionsMap.current,
        signalingClient,
        onParentAudioTrack: onParentAudioTrackRef.current ?? undefined,
        updateConnectedPeersCount,
      });
    },
    [updateConnectedPeersCount],
  );

  /**
   * Handle offer from a parent device (baby mode)
   */
  const handleOfferFromParent = useCallback(
    async (parentId: string, sdp: RTCSessionDescriptionInit) => {
      try {
        debugLog(`[WebRTC/Baby] Handling offer from parent: ${parentId}`);

        // Check if we have a stream
        if (!localStreamRef.current) {
          debugLog(
            `[WebRTC/Baby] No stream yet, queuing offer from ${parentId}`,
          );
          pendingOffersRef.current.set(parentId, { sourceId: parentId, sdp });
          return;
        }

        // Get or create peer connection for this parent
        let pcInfo = peerConnectionsMap.current.get(parentId);
        let pc: RTCPeerConnection;

        if (
          !pcInfo ||
          pcInfo.pc.connectionState === "closed" ||
          pcInfo.pc.connectionState === "failed"
        ) {
          pc = createPeerConnectionForParent(parentId);
          pcInfo = peerConnectionsMap.current.get(parentId)!;
        } else {
          pc = pcInfo.pc;
        }

        // Set remote description
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));

        // Flush pending ICE candidates
        await flushPendingIceCandidates(pc, pcInfo.pendingIceCandidates);
        pcInfo.pendingIceCandidates = [];

        // Create and send answer
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        debugLog(`[WebRTC/Baby] Sending answer to parent ${parentId}`);
        signalingClient.send({
          type: "answer",
          targetId: parentId,
          sdp: pc.localDescription || answer,
        });

        updateConnectedPeersCount();
      } catch (err) {
        debugError(`[WebRTC/Baby] Error handling offer from ${parentId}`, err);
      }
    },
    [
      createPeerConnectionForParent,
      flushPendingIceCandidates,
      updateConnectedPeersCount,
    ],
  );

  // ============================================================
  // Parent Mode: Single peer handling
  // ============================================================

  /**
   * Create peer connection for parent mode
   */
  const createPeerConnection = useCallback(() => {
    // Close existing connection if any
    if (peerConnection.current) {
      debugLog("[WebRTC/Parent] Closing existing peer connection");
      peerConnection.current.close();
    }

    const pc = createParentPeerConnection({
      setConnectionState,
      onRemoteStream: onRemoteStreamRef.current ?? undefined,
      onVideoTrackReceived: onVideoTrackReceivedRef.current ?? undefined,
      onDisconnected: () => {
        remotePeerIdRef.current = null;
        pendingIceCandidatesRef.current = [];
      },
    });

    peerConnection.current = pc;
    return pc;
  }, []);

  // ============================================================
  // Lifecycle Effects
  // ============================================================

  // Initialize on mount
  useEffect(() => {
    if (role === "parent") {
      createPeerConnection();
    }

    // Handle tab close - graceful disconnect
    const handleBeforeUnload = () => {
      debugLog("[WebRTC] Page unloading, sending disconnect notification");

      if (role === "parent" && remotePeerIdRef.current) {
        signalingClient.send({
          type: "peer-disconnect",
          targetId: remotePeerIdRef.current,
        });
        peerConnection.current?.close();
      }

      if (role === "baby") {
        peerConnectionsMap.current.forEach((info, parentId) => {
          signalingClient.send({
            type: "peer-disconnect",
            targetId: parentId,
          });
          info.pc.close();
        });
        peerConnectionsMap.current.clear();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);

      if (role === "parent" && peerConnection.current) {
        peerConnection.current.close();
        peerConnection.current = null;
      }

      if (role === "baby") {
        peerConnectionsMap.current.forEach((info) => info.pc.close());
        peerConnectionsMap.current.clear();
      }

      pendingIceCandidatesRef.current = [];
    };
  }, [createPeerConnection, role]);

  // Process pending offers when stream becomes available (baby mode)
  useEffect(() => {
    if (role !== "baby" || !localStream) return;

    debugLog(
      `[WebRTC/Baby] Stream available: ${localStream
        .getTracks()
        .map((t) => t.kind)
        .join(", ")}`,
    );

    // Process any pending offers
    if (pendingOffersRef.current.size > 0) {
      debugLog(
        `[WebRTC/Baby] Processing ${pendingOffersRef.current.size} pending offers`,
      );
      const pendingOffers = Array.from(pendingOffersRef.current.values());
      pendingOffersRef.current.clear();

      pendingOffers.forEach(({ sourceId, sdp }) => {
        handleOfferFromParent(sourceId, sdp);
      });
    }
  }, [localStream, role, handleOfferFromParent]);

  // Connect to signaling server
  useEffect(() => {
    const connectSignaling = async () => {
      const token = await getToken();
      if (token) {
        signalingClient.setTokenProvider(getToken);
        signalingClient.connect(token, role);
        signalingConnectedRef.current = true;
      }
    };
    connectSignaling();

    return () => {
      signalingClient.disconnect();
      signalingConnectedRef.current = false;
    };
  }, [getToken, role]);

  // Handle signaling messages
  useEffect(() => {
    const handleOffer = async (msg: any) => {
      debugLog("[WebRTC] Received OFFER", msg);

      if (role === "baby") {
        await handleOfferFromParent(msg.sourceId, msg.sdp);
      } else {
        // Parent shouldn't receive offers in simplified mode
        debugLog("[WebRTC/Parent] Unexpected offer received");
      }
    };

    const handleAnswer = async (msg: any) => {
      try {
        debugLog("[WebRTC] Received ANSWER", msg);

        if (role === "parent") {
          // Mark answer received to stop retry attempts
          answerReceivedRef.current = true;
          if (offerRetryTimeoutRef.current) {
            clearTimeout(offerRetryTimeoutRef.current);
            offerRetryTimeoutRef.current = null;
          }

          const pc = peerConnection.current;
          if (!pc) {
            debugError("[WebRTC/Parent] No peer connection for ANSWER");
            return;
          }

          if (pc.signalingState === "have-local-offer") {
            await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
            await flushPendingIceCandidates(
              pc,
              pendingIceCandidatesRef.current,
            );
            pendingIceCandidatesRef.current = [];
            debugLog("[WebRTC/Parent] Answer processed successfully");
          } else {
            debugLog(
              `[WebRTC/Parent] Ignoring ANSWER in state: ${pc.signalingState}`,
            );
          }
        }
      } catch (err) {
        debugError("[WebRTC] Error handling ANSWER", err);
      }
    };

    const handleIce = async (msg: any) => {
      debugLog("[WebRTC] Received ICE Candidate");

      if (role === "baby") {
        const pcInfo = peerConnectionsMap.current.get(msg.sourceId);
        if (!pcInfo) {
          debugLog(
            `[WebRTC/Baby] No peer connection for ${msg.sourceId}, ignoring ICE`,
          );
          return;
        }

        try {
          if (pcInfo.pc.remoteDescription) {
            await pcInfo.pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
          } else {
            debugLog(`[WebRTC/Baby] Buffering ICE for ${msg.sourceId}`);
            pcInfo.pendingIceCandidates.push(msg.candidate);
          }
        } catch (e) {
          debugError("[WebRTC/Baby] Failed to add ICE candidate", e);
        }
      } else {
        const pc = peerConnection.current;
        if (!pc) {
          debugLog("[WebRTC/Parent] No peer connection for ICE, buffering");
          pendingIceCandidatesRef.current.push(msg.candidate);
          return;
        }

        try {
          if (pc.remoteDescription) {
            await pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
          } else {
            debugLog("[WebRTC/Parent] Buffering ICE");
            pendingIceCandidatesRef.current.push(msg.candidate);
          }
        } catch (e) {
          debugError("[WebRTC/Parent] Failed to add ICE candidate", e);
        }
      }
    };

    const handlePeerDisconnected = (msg: any) => {
      debugLog("[WebRTC] Peer disconnected", msg);

      if (role === "baby") {
        const pcInfo = peerConnectionsMap.current.get(msg.sourceId);
        if (pcInfo) {
          pcInfo.pc.close();
          peerConnectionsMap.current.delete(msg.sourceId);
          updateConnectedPeersCount();
          // Reset talk state for this parent just in case they were talking
          if (onParentTalkStateRef.current) {
            onParentTalkStateRef.current(false, msg.sourceId);
          }
        }
      } else {
        if (peerConnection.current) {
          peerConnection.current.close();
        }
        createPeerConnection();
        remotePeerIdRef.current = null;
        pendingIceCandidatesRef.current = [];
        setConnectionState("idle");
      }
    };

    // Handle parent talk state (baby mode only)
    const handleParentTalkState = (msg: any) => {
      if (role !== "baby") return;

      debugLog(
        `[WebRTC/Baby] Parent ${msg.sourceId} ${msg.talking ? "started" : "stopped"} talking`,
      );

      if (onParentTalkStateRef.current) {
        onParentTalkStateRef.current(msg.talking, msg.sourceId);
      }
    };

    signalingClient.on("offer", handleOffer);
    signalingClient.on("answer", handleAnswer);
    signalingClient.on("ice", handleIce);
    signalingClient.on("peer-disconnected", handlePeerDisconnected);
    signalingClient.on("parent-talk-state", handleParentTalkState);

    return () => {
      signalingClient.off("offer", handleOffer);
      signalingClient.off("answer", handleAnswer);
      signalingClient.off("ice", handleIce);
      signalingClient.off("peer-disconnected", handlePeerDisconnected);
      signalingClient.off("parent-talk-state", handleParentTalkState);
    };
  }, [
    role,
    flushPendingIceCandidates,
    createPeerConnection,
    handleOfferFromParent,
    updateConnectedPeersCount,
  ]);

  // ============================================================
  // Public API
  // ============================================================

  /**
   * Send offer with retry logic
   */
  const sendOfferWithRetry = useCallback(
    async (
      pc: RTCPeerConnection,
      targetId: string,
      isRetry: boolean = false,
    ) => {
      // If this is a retry, we need to create a new offer (potentially with ICE restart)
      if (isRetry) {
        debugLog(
          `[WebRTC/Parent] Retrying offer (attempt ${offerRetryCountRef.current + 1}/${MAX_OFFER_RETRIES})`,
        );

        // Create new offer with ICE restart for better connectivity
        const offer = await pc.createOffer({ iceRestart: true });
        await pc.setLocalDescription(offer);
      }

      debugLog("[WebRTC/Parent] Sending OFFER");
      signalingClient.send({
        type: "offer",
        targetId,
        sdp: pc.localDescription,
      });

      // Schedule retry if answer not received
      offerRetryTimeoutRef.current = setTimeout(async () => {
        // Check if answer was received or connection established
        if (answerReceivedRef.current || pc.connectionState === "connected") {
          debugLog(
            "[WebRTC/Parent] Answer received or connected, no retry needed",
          );
          return;
        }

        offerRetryCountRef.current++;

        if (offerRetryCountRef.current >= MAX_OFFER_RETRIES) {
          debugLog("[WebRTC/Parent] Max offer retries reached, giving up");
          setConnectionState("failed");
          return;
        }

        // Retry if still in have-local-offer state and not connected
        if (
          pc.signalingState === "have-local-offer" &&
          currentTargetIdRef.current === targetId
        ) {
          debugLog(
            "[WebRTC/Parent] No answer received, retrying with ICE restart...",
          );
          await sendOfferWithRetry(pc, targetId, true);
        }
      }, OFFER_RETRY_DELAY_MS);
    },
    [],
  );

  /**
   * Start calling a target baby device (parent mode)
   */
  const call = useCallback(
    async (targetId: string) => {
      if (role !== "parent") {
        debugError("[WebRTC] call() should only be used by parent");
        return;
      }

      debugLog(`[WebRTC/Parent] Starting call to ${targetId}`);

      // Reset retry tracking
      answerReceivedRef.current = false;
      offerRetryCountRef.current = 0;
      currentTargetIdRef.current = targetId;
      if (offerRetryTimeoutRef.current) {
        clearTimeout(offerRetryTimeoutRef.current);
        offerRetryTimeoutRef.current = null;
      }

      // Ensure we have a fresh peer connection
      let pc = peerConnection.current;
      if (
        !pc ||
        pc.connectionState === "closed" ||
        pc.connectionState === "failed" ||
        pc.signalingState !== "stable"
      ) {
        debugLog("[WebRTC/Parent] Creating fresh peer connection");
        pc = createPeerConnection();
      }

      remotePeerIdRef.current = targetId;
      pendingIceCandidatesRef.current = [];

      // Set up ICE candidate handler
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          debugLog("[WebRTC/Parent] Sending ICE candidate");
          signalingClient.send({
            type: "ice",
            targetId,
            candidate: event.candidate,
          });
        }
      };

      // Add receive-only transceivers for Safari compatibility
      ensureReceiveOnlyTransceivers(pc);

      // Create initial offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Send offer with retry logic
      await sendOfferWithRetry(pc, targetId, false);
    },
    [createPeerConnection, role, sendOfferWithRetry],
  );

  /**
   * Disconnect from current call
   */
  const disconnect = useCallback(() => {
    debugLog("[WebRTC] Disconnect called");

    // Clear any pending retry timeouts
    if (offerRetryTimeoutRef.current) {
      clearTimeout(offerRetryTimeoutRef.current);
      offerRetryTimeoutRef.current = null;
    }
    answerReceivedRef.current = false;
    offerRetryCountRef.current = 0;
    currentTargetIdRef.current = null;

    if (role === "parent") {
      if (remotePeerIdRef.current) {
        signalingClient.send({
          type: "peer-disconnect",
          targetId: remotePeerIdRef.current,
        });
      }

      if (peerConnection.current) {
        peerConnection.current.close();
      }

      createPeerConnection();
      remotePeerIdRef.current = null;
      pendingIceCandidatesRef.current = [];
      setConnectionState("idle");
    } else {
      // Baby: disconnect all parents
      peerConnectionsMap.current.forEach((info, parentId) => {
        signalingClient.send({
          type: "peer-disconnect",
          targetId: parentId,
        });
        info.pc.close();
      });
      peerConnectionsMap.current.clear();
      setConnectedPeersCount(0);
      setConnectionState("idle");
      // Mark streaming as inactive so PWA can update
      setStreamingActive(false);
    }
  }, [createPeerConnection, role]);

  /**
   * Force signaling reconnection
   */
  const reconnectSignaling = useCallback(async () => {
    debugLog("[WebRTC] Reconnecting signaling");
    signalingClient.forceReconnect();
    signalingConnectedRef.current = true;
  }, []);

  // ============================================================
  // Parent Talk Mode Functions
  // ============================================================

  /**
   * Start talking to baby (parent mode only)
   * Captures parent's microphone and adds audio track to peer connection
   */
  const startTalking = useCallback(async () => {
    if (role !== "parent") {
      debugError("[WebRTC] startTalking() should only be used by parent");
      return;
    }

    const pc = peerConnection.current;
    if (!pc || pc.connectionState !== "connected") {
      debugError("[WebRTC/Parent] Cannot start talking - not connected");
      return;
    }

    if (!remotePeerIdRef.current) {
      debugError("[WebRTC/Parent] Cannot start talking - no remote peer");
      return;
    }

    try {
      debugLog("[WebRTC/Parent] Starting talk mode - capturing microphone");

      // Capture parent's microphone
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      parentMicStreamRef.current = micStream;

      const audioTrack = micStream.getAudioTracks()[0];
      if (!audioTrack) {
        debugError("[WebRTC/Parent] No audio track from microphone");
        return;
      }

      debugLog(
        "[WebRTC/Parent] Got microphone track, adding to peer connection",
      );

      // Find existing audio transceiver or add new one
      const transceivers = pc.getTransceivers();
      let audioTransceiver = transceivers.find(
        (t) =>
          t.receiver.track?.kind === "audio" ||
          t.sender.track?.kind === "audio",
      );

      if (audioTransceiver) {
        // Change direction to sendrecv and replace track
        debugLog(
          "[WebRTC/Parent] Found existing audio transceiver, updating to sendrecv",
        );
        audioTransceiver.direction = "sendrecv";
        await audioTransceiver.sender.replaceTrack(audioTrack);
      } else {
        // Add new track
        debugLog("[WebRTC/Parent] No audio transceiver found, adding track");
        pc.addTrack(audioTrack, micStream);
      }

      // NEGOTIATION: We must renegotiate to send the new track
      debugLog("[WebRTC/Parent] Negotiating new audio track...");
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await sendOfferWithRetry(pc, remotePeerIdRef.current, false);

      // Signal to baby that parent is talking
      signalingClient.send({
        type: "parent-talk-state",
        targetId: remotePeerIdRef.current,
        talking: true,
      });

      setIsTalking(true);
      debugLog("[WebRTC/Parent] Talk mode started successfully");
    } catch (err) {
      debugError("[WebRTC/Parent] Failed to start talking", err);
      // Cleanup on error
      if (parentMicStreamRef.current) {
        parentMicStreamRef.current.getTracks().forEach((t) => t.stop());
        parentMicStreamRef.current = null;
      }
    }
  }, [role]);

  /**
   * Stop talking to baby (parent mode only)
   * Stops microphone and removes audio track from peer connection
   */
  const stopTalking = useCallback(() => {
    if (role !== "parent") {
      debugError("[WebRTC] stopTalking() should only be used by parent");
      return;
    }

    debugLog("[WebRTC/Parent] Stopping talk mode");

    // Stop and cleanup microphone stream
    if (parentMicStreamRef.current) {
      parentMicStreamRef.current.getTracks().forEach((track) => {
        debugLog(`[WebRTC/Parent] Stopping mic track: ${track.kind}`);
        track.stop();
      });
      parentMicStreamRef.current = null;
    }

    // Remove track from peer connection
    const pc = peerConnection.current;
    if (pc) {
      const senders = pc.getSenders();
      const audioSender = senders.find((s) => s.track?.kind === "audio");
      if (audioSender) {
        debugLog("[WebRTC/Parent] Removing audio track from sender");
        audioSender.replaceTrack(null).catch((err) => {
          debugError("[WebRTC/Parent] Failed to remove audio track", err);
        });
      }

      // Revert transceiver direction to recvonly
      const audioTransceiver = pc
        .getTransceivers()
        .find((t) => t.receiver.track?.kind === "audio");
      if (audioTransceiver) {
        audioTransceiver.direction = "recvonly";
      }

      // NEGOTIATION: We must renegotiate to revert changes
      // Note: We use an async IIFE or just call it, but since stopTalking is sync in UI, we catch errors
      pc.createOffer()
        .then((offer) => pc.setLocalDescription(offer))
        .then(() => sendOfferWithRetry(pc, remotePeerIdRef.current!, false))
        .catch((err) =>
          debugError("[WebRTC/Parent] Failed to renegotiate stop talk", err),
        );
    }

    // Signal to baby that parent stopped talking
    if (remotePeerIdRef.current) {
      signalingClient.send({
        type: "parent-talk-state",
        targetId: remotePeerIdRef.current,
        talking: false,
      });
    }

    setIsTalking(false);
    debugLog("[WebRTC/Parent] Talk mode stopped");
  }, [role]);

  return {
    connectionState,
    connectedPeersCount,
    error,
    call,
    disconnect,
    reconnectSignaling,
    signalingClient,
    // Parent talk mode
    isTalking,
    startTalking,
    stopTalking,
  };
}
